import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { DealContextBar } from "@/components/DealContextBar";
import { SidePanelSkeleton } from "@/components/LazyLoadFallbacks";
import { CopilotTrigger } from "@/components/CopilotTrigger";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { supabase } from "@/integrations/supabase/client";

const CopilotPanel = lazy(() => import("@/components/CopilotPanel").then((module) => ({ default: module.CopilotPanel })));
const CopilotIntelligencePanel = lazy(() =>
  import("@/components/CopilotIntelligencePanel").then((m) => ({ default: m.CopilotIntelligencePanel })),
);

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const [copilotOpen, setCopilotOpen] = useState(false);
  const location = useLocation();
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(
    typeof window !== "undefined" ? localStorage.getItem("brickshare_active_workspace") : null,
  );

  // Track active deal id (sessionStorage) so the intelligence panel reacts to context.
  const [activeDealId, setActiveDealId] = useState<string | null>(
    typeof window !== "undefined" ? sessionStorage.getItem("brickshare_deal_id") : null,
  );
  useEffect(() => {
    const sync = () => setActiveDealId(sessionStorage.getItem("brickshare_deal_id"));
    sync();
    window.addEventListener("storage", sync);
    const interval = window.setInterval(sync, 1500);
    return () => {
      window.removeEventListener("storage", sync);
      window.clearInterval(interval);
    };
  }, [location.pathname]);

  useEffect(() => {
    const syncWorkspace = () => setActiveWorkspaceId(localStorage.getItem("brickshare_active_workspace"));
    syncWorkspace();
    window.addEventListener("storage", syncWorkspace);
    const interval = window.setInterval(syncWorkspace, 1000);
    return () => {
      window.removeEventListener("storage", syncWorkspace);
      window.clearInterval(interval);
    };
  }, []);

  const intelScope: "deal" | "all" | "documents" = /\/analytics|\/dashboard|\/my-deals|\/reports/.test(
    location.pathname,
  )
    ? "all"
    : /\/admin\/scans|\/documents/.test(location.pathname)
      ? "documents"
      : activeDealId
        ? "deal"
        : "all";

  useEffect(() => {
    const loadThresholds = async () => {
      if (sessionStorage.getItem("brickshare_thresholds")) return;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await (supabase as any)
        .from("user_settings")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        sessionStorage.setItem("brickshare_thresholds", JSON.stringify({
          target_irr: Number(data.target_irr),
          target_coc: Number(data.target_coc),
          target_dscr: Number(data.target_dscr),
          proceed_cutoff: Number(data.proceed_cutoff),
          caution_cutoff: Number(data.caution_cutoff),
          stabilized_vacancy_default: Number(data.stabilized_vacancy_default),
          selling_costs_pct: Number(data.selling_costs_pct),
        }));
      }
    };
    loadThresholds();
  }, []);

  return (
    <SidebarProvider>
      <div className="h-screen flex w-full overflow-hidden">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0 h-full">
          <header className="h-12 flex items-center justify-between border-b border-[hsl(var(--stone-200))]/70 bg-[hsl(var(--bone))]/85 backdrop-blur-md sticky top-0 z-50 shrink-0">
            <SidebarTrigger className="ml-3 text-[hsl(var(--stone-600))] hover:text-[hsl(var(--ink))]" />
            <div className="mr-3">
              <CopilotTrigger onClick={() => setCopilotOpen(!copilotOpen)} isOpen={copilotOpen} />
            </div>
          </header>
          <DealContextBar />
          <div className="flex-1 flex min-h-0">
            <main className="flex-1 overflow-auto min-w-0">
              {children}
            </main>
            {copilotOpen && (
              <Suspense fallback={<SidePanelSkeleton />}>
                <CopilotPanel open={copilotOpen} onClose={() => setCopilotOpen(false)} />
                <CopilotIntelligencePanel
                  dealId={activeDealId}
                  scope={intelScope}
                  activeWorkspaceId={activeWorkspaceId}
                />
              </Suspense>
            )}
          </div>
        </div>
      </div>
      <FeedbackDialog />
    </SidebarProvider>
  );
}
